import { useEffect, useState } from "react";
import {
    AlertTriangle,
    ArrowDownRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    Info,
    Minus,
    Shield,
    TrendingDown,
    TrendingUp,
    Zap,
} from "lucide-react";
import { InteractiveAreaChart } from "./statusHelpers";
import { myLocalStorage } from "../../StorageHelper";
import { NGROK } from "../../../APIs";
import axios from "axios";
import "./AlertTrendPanel.css";

// ─── Types ──────────────────────────────────────────────

interface DayBucket {
    label: string;
    alertCount: number;
}

interface AlertTrendPanelProps {
    cloudProvider: string;
    onCompare?: () => void;
}

// ─── Helpers ────────────────────────────────────────────

function getTrend(current: number, previous: number): "improving" | "worsening" | "stable" {
    const delta = current - previous;
    if (delta < -1) return "improving";
    if (delta > 1) return "worsening";
    return "stable";
}

/** Build 7 day-label buckets (7d ago … Today) with counts from a flat alert list */
function bucketAlertsByDay(alerts: any[]): DayBucket[] {
    const now = new Date();
    const buckets: DayBucket[] = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now);
        d.setDate(now.getDate() - (6 - i));
        const label = i === 6 ? "Today" : `${6 - i}d ago`;
        return { label, alertCount: 0, _date: d.toDateString() } as any;
    });

    alerts.forEach((alert) => {
        const raw = alert.createdAt || alert.timestamp || alert.date;
        if (!raw) return;
        const dateStr = new Date(raw).toDateString();
        const bucket = (buckets as any[]).find((b) => b._date === dateStr);
        if (bucket) bucket.alertCount += 1;
    });

    // Strip internal _date field
    return buckets.map(({ label, alertCount }) => ({ label, alertCount }));
}

// ─── Component ──────────────────────────────────────────

export default function AlertTrendPanel({ cloudProvider }: AlertTrendPanelProps) {
    const [snapshots, setSnapshots] = useState<DayBucket[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tenantName =
            myLocalStorage.getItem("latestTenant")?.tenantName ||
            myLocalStorage.getItem("latestTenant");

        const cp = cloudProvider?.toLowerCase();
        let cloudId: string | null = null;
        if (cp === "gcp") {
            cloudId = myLocalStorage.getItem("gcpSelectedProjectId");
        } else if (cp === "aws") {
            cloudId = myLocalStorage.getItem("selectedAwsAccount");
        } else if (cp === "azure") {
            const sub = myLocalStorage.getItem("latestSubList");
            cloudId = sub?.azureSubscriptionId || null;
        }

        if (!tenantName || !cloudId) {
            setLoading(false);
            return;
        }

        const fetchAlerts = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${NGROK}/api/cloud/get-anomalies-group`, {
                    params: {
                        tenantName,
                        cloudId,
                        cloudType: cloudProvider?.toUpperCase(),
                        timeRangeCode: 6, // Last 7 days
                        groupBy: "alertType",
                    },
                });

                const groupRaw = response.data || [];
                let flattened: any[] = [];
                if (Array.isArray(groupRaw)) {
                    flattened = groupRaw;
                } else {
                    Object.values(groupRaw).forEach((val) => {
                        if (Array.isArray(val)) flattened = flattened.concat(val);
                    });
                }

                setSnapshots(bucketAlertsByDay(flattened));
            } catch {
                setSnapshots(bucketAlertsByDay([]));
            } finally {
                setLoading(false);
            }
        };

        fetchAlerts();
    }, [cloudProvider]);

    // ── Loading state ───────────────────────────────────
    if (loading) {
        return (
            <div className="snapshot-comparison">
                <div className="sc-insights" style={{ flex: 1 }}>
                    <div className="sc-insights-header">
                        <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                        <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                    </div>
                    <div className="sc-insight-items">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="h-4 bg-muted/50 animate-pulse rounded-md mb-2" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // ── No data state ───────────────────────────────────
    const hasData = snapshots.some((s) => s.alertCount > 0);

    if (!hasData) {
        return (
            <div className="snapshot-comparison">
                <div className="sc-insights" style={{ flex: 1 }}>
                    <div className="sc-insights-header">
                        <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                        <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                        <div className="sc-trend-badge stable">
                            <Minus /> Stable
                        </div>
                    </div>
                    <div className="sc-insight-items">
                        <div className="sc-insight-item">
                            <Shield />
                            <span>No alerts detected in the last 7 days — your cloud posture looks <strong>healthy</strong></span>
                        </div>
                    </div>
                </div>
                <div className="sc-divider" />
                <div className="sc-scores">
                    <div className="sc-chart-wrapper">
                        <InteractiveAreaChart
                            values={snapshots.map((s) => s.alertCount)}
                            labels={snapshots.map((s) => s.label === "Today" ? "TODAY" : s.label.split(" ")[0].toUpperCase())}
                            color="hsl(var(--primary))"
                            gradientId="sc-trend-chart-gradient"
                            width={600}
                            height={240}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // ── Full data view ──────────────────────────────────
    const latest = snapshots[snapshots.length - 1];
    const previous = snapshots[snapshots.length - 2];
    const oldest = snapshots[0];

    const counts = snapshots.map((s) => s.alertCount);
    const maxCount = Math.max(...counts);
    const minCount = Math.min(...counts);
    const avgCount = Math.round(counts.reduce((a, b) => a + b, 0) / counts.length);

    const delta = latest.alertCount - previous.alertCount;
    const trend = getTrend(latest.alertCount, previous.alertCount);
    const overallDelta = latest.alertCount - oldest.alertCount;

    const peakIdx = counts.indexOf(maxCount);
    const lowIdx = counts.indexOf(minCount);

    let maxJump = 0;
    let maxJumpDay = "";
    for (let i = 1; i < snapshots.length; i++) {
        const jmp = snapshots[i].alertCount - snapshots[i - 1].alertCount;
        if (jmp > maxJump) {
            maxJump = jmp;
            maxJumpDay = snapshots[i].label;
        }
    }

    const spread = maxCount - minCount;
    const volatilityPct = avgCount > 0 ? Math.round((spread / avgCount) * 100) : 0;

    return (
        <div className="snapshot-comparison">
            {/* Left: Insights */}
            <div className="sc-insights">
                <div className="sc-insights-header">
                    <AlertTriangle className="w-4 h-4" style={{ color: "hsl(var(--primary))" }} />
                    <span className="sc-insights-title">Alert Trend Analysis · Last 7 Days</span>
                    <div className={`sc-trend-badge ${trend}`}>
                        {trend === "improving" ? (
                            <><TrendingDown /> Improving</>
                        ) : trend === "worsening" ? (
                            <><TrendingUp /> Worsening</>
                        ) : (
                            <><Minus /> Stable</>
                        )}
                    </div>
                </div>

                <div className="sc-insight-items">
                    <div className="sc-insight-item">
                        <Bell />
                        <span>
                            Today's snapshot has <strong>{latest.alertCount} alerts</strong>
                            {delta !== 0 && (
                                <>
                                    {" "}
                                    <span className={`sc-delta ${delta > 0 ? "positive" : "negative"}`}>
                                        {delta > 0 ? (
                                            <><ArrowUpRight className="inline w-3 h-3" /> +{delta}</>
                                        ) : (
                                            <><ArrowDownRight className="inline w-3 h-3" /> {delta}</>
                                        )}
                                    </span>
                                    {" "}vs yesterday
                                </>
                            )}
                            {delta === 0 && " — unchanged from yesterday"}
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <BarChart3 />
                        <span>
                            {overallDelta > 0
                                ? <><strong>+{overallDelta}</strong> alerts over the week — investigate recent permission grants or configuration changes</>
                                : overallDelta < 0
                                    ? <><strong>{Math.abs(overallDelta)}</strong> fewer alerts over the week — remediation efforts are working</>
                                    : <>Alert count has been <strong>stable</strong> across the last 7 days</>
                            }
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <Zap />
                        <span>
                            Peak: <strong>{maxCount} alerts</strong> ({snapshots[peakIdx].label}) · Lowest: <strong>{minCount} alerts</strong> ({snapshots[lowIdx].label})
                        </span>
                    </div>

                    <div className="sc-insight-item">
                        <Shield />
                        <span>
                            7-day average: <strong>{avgCount} alerts</strong>
                            {latest.alertCount > avgCount
                                ? <> — today is <strong>{latest.alertCount - avgCount} above</strong> average</>
                                : latest.alertCount < avgCount
                                    ? <> — today is <strong>{avgCount - latest.alertCount} below</strong> average</>
                                    : <> — today is at average</>
                            }
                        </span>
                    </div>

                    {maxJump > 0 && (
                        <div className="sc-insight-item">
                            <TrendingUp />
                            <span>
                                Largest single-day spike: <strong>+{maxJump} alerts</strong> ({maxJumpDay}) — correlate with recent deployments
                            </span>
                        </div>
                    )}

                    {volatilityPct > 0 && (
                        <div className="sc-insight-item">
                            <Info />
                            <span>
                                Alert volatility: <strong>{volatilityPct}%</strong> — {volatilityPct > 30 ? "high variance indicates inconsistent security posture" : "steady trend indicates predictable access patterns"}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="sc-divider" />

            {/* Right: Alert Trend Chart */}
            <div className="sc-scores">
                <div className="sc-chart-wrapper">
                    <InteractiveAreaChart
                        values={snapshots.map((s) => s.alertCount)}
                        labels={snapshots.map((s) => s.label === "Today" ? "TODAY" : s.label.split(" ")[0].toUpperCase())}
                        color="hsl(var(--primary))"
                        gradientId="sc-trend-chart-gradient"
                        width={600}
                        height={240}
                    />
                </div>
            </div>
        </div>
    );
}
